/**
 * Routes of area (f) — access. OWNED BY area (f) — access: tighten `validateSearch`, add loaders, swap `component`
 * for the real screen (keep the paths — boards.ts hrefs point at them). Generated by the foundation.
 */
import { lazy, Suspense, type ComponentType } from "react";
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { boardsAt } from "@/boards/board-switch";
import { guard, looseSearch, publicRoute, staffRoute } from "@/app/route-base";
import { PageSkeleton } from "@/ui/page";

/**
 * One screen for the boards that are states of it, so a window or the tour opening / closing (search param change)
 * never remounts the page: the page reads `?fenetre=` / `?didacticiel=` itself.
 */
function screen(load: () => Promise<{ default: ComponentType }>) {
  const C = lazy(load);
  return function Screen() {
    return (
      <Suspense fallback={<PageSkeleton />}>
        <C />
      </Suspense>
    );
  };
}

/** Search params kept loose (other keys survive) with the known ones typed. */
const withLoose = <S extends z.ZodRawShape>(shape: S) => (search: Record<string, unknown>) => ({ ...looseSearch(search), ...z.object(shape).catch({} as never).parse(search) });
const tourSearch = withLoose({ didacticiel: z.coerce.string().optional(), etape: z.coerce.string().optional() });

/** Admin-Parametres-Equipe, AG-Equipe */
export const parametresEquipeRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/parametres/equipe",
  validateSearch: withLoose({ fenetre: z.enum(["creer-acces"]).optional().catch(undefined) }),
  beforeLoad: guard("team", "read"),
  component: screen(() => import("./boards/Admin-Parametres-Equipe")),
});

/** AG-Connexion */
export const connexionPremiereConnexionRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/connexion/premiere-connexion",
  validateSearch: looseSearch,
  component: boardsAt("/connexion/premiere-connexion"),
});

/** AG-Didacticiel, AG-Accueil */
export const creationAFaireRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/a-faire",
  validateSearch: tourSearch,
  beforeLoad: guard("creation.followup", "read"),
  component: screen(() => import("./boards/AG-Accueil")),
});

/** AG-Demandes */
export const creationDemandesRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/demandes",
  validateSearch: looseSearch,
  beforeLoad: guard("creation", "read"),
  component: boardsAt("/creation/demandes"),
});

/** AG-Fiche */
export const creationDemandesDossieridRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/demandes/$dossierId",
  validateSearch: looseSearch,
  beforeLoad: guard("creation", "read"),
  component: boardsAt("/creation/demandes/$dossierId"),
});

/** AG-Lecteur */
export const creationLectureRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/lecture",
  validateSearch: looseSearch,
  beforeLoad: guard("creation", "read"),
  component: boardsAt("/creation/lecture"),
});

/** AG-Discussions */
export const creationDiscussionsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/discussions",
  validateSearch: looseSearch,
  beforeLoad: guard("discussions", "read"),
  component: boardsAt("/creation/discussions"),
});

/** AG-Rappels */
export const creationRappelsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/creation/rappels",
  validateSearch: looseSearch,
  beforeLoad: guard("creation.followup", "read"),
  component: boardsAt("/creation/rappels"),
});

/** AG-Matrice */
export const referenceRolesMatriceRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/roles/matrice",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/roles/matrice"),
});

/** AG-Roles-Reference */
export const referenceRolesRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/roles",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/roles"),
});

/** AC-Equipe */
export const referenceClientEquipeRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/client/equipe",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/client/equipe"),
});

/** AC-Achat-Siege */
export const referenceClientSiegeRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/client/siege",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/client/siege"),
});

/** AC-Experts */
export const referenceClientExpertsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/client/experts",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/client/experts"),
});

/** AC-Invitation */
export const referenceClientInvitationRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/client/invitation",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/client/invitation"),
});

/** AC-Connexion */
export const cabinetConnexionRoute = createRoute({
  getParentRoute: () => publicRoute,
  path: "/cabinet/connexion",
  validateSearch: looseSearch,
  component: boardsAt("/cabinet/connexion"),
});

/** AC-Didacticiel, AC-Cockpit */
export const cabinetRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/cabinet",
  validateSearch: tourSearch,
  beforeLoad: guard("cabinet.dashboard", "read"),
  component: screen(() => import("./boards/AC-Cockpit")),
});

/** AC-Revue */
export const cabinetRevueRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/cabinet/revue",
  validateSearch: looseSearch,
  beforeLoad: guard("cabinet.operations", "read"),
  component: boardsAt("/cabinet/revue"),
});

/** AC-Rattachements */
export const comptaRattachementsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/rattachements",
  validateSearch: looseSearch,
  beforeLoad: guard("cabinet.access", "read"),
  component: boardsAt("/compta/rattachements"),
});

/** AC-Affectation */
export const comptaAffectationRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/affectation",
  validateSearch: looseSearch,
  beforeLoad: guard("cabinet.access", "read"),
  component: boardsAt("/compta/affectation"),
});

/** AC-Matrice */
export const referenceCabinetMatriceRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/reference/cabinet/matrice",
  validateSearch: looseSearch,
  beforeLoad: guard("reference", "read"),
  component: boardsAt("/reference/cabinet/matrice"),
});

export const accessRoutes = [parametresEquipeRoute, creationAFaireRoute, creationDemandesRoute, creationDemandesDossieridRoute, creationLectureRoute, creationDiscussionsRoute, creationRappelsRoute, referenceRolesMatriceRoute, referenceRolesRoute, referenceClientEquipeRoute, referenceClientSiegeRoute, referenceClientExpertsRoute, referenceClientInvitationRoute, cabinetRoute, cabinetRevueRoute, comptaRattachementsRoute, comptaAffectationRoute, referenceCabinetMatriceRoute] as const;

/** Sign-in pages (no session): mounted under publicRoute. */
export const accessPublicRoutes = [connexionPremiereConnexionRoute, cabinetConnexionRoute] as const;
